/**
 * MKS-508 — link and anchor rules for the company docs reader (pure, unit-tested).
 *
 * The docs are written for GitHub: relative links to sibling docs, to private
 * docs and to repo files, plus GitHub-style `#heading` anchors. In the app only
 * the first and the anchors can work, so every link is classified here.
 */

/**
 * GitHub's heading slug: lowercase, drop everything that is not a letter, mark,
 * number, connector punctuation, space or hyphen, then spaces become hyphens.
 * `seen` is a per-document map; a repeated slug gets `-1`, `-2`, ... so anchors
 * written for GitHub keep resolving here.
 */
export function githubSlug(text: string, seen: Map<string, number>): string {
  const base = text
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}\p{Pc} -]/gu, '')
    .replace(/ /g, '-');
  const count = seen.get(base);
  if (count === undefined) {
    seen.set(base, 0);
    return base;
  }
  let next = count + 1;
  let candidate = `${base}-${next}`;
  while (seen.has(candidate)) {
    next += 1;
    candidate = `${base}-${next}`;
  }
  seen.set(base, next);
  seen.set(candidate, 0);
  return candidate;
}

export type DocLink =
  | { kind: 'doc'; to: string }
  | { kind: 'anchor'; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'repo-only' };

const EXTERNAL_PROTOCOL = /^(https?:|mailto:)/i;
const PUBLIC_DOC_HREF = /^([^/#?]+)\.md(#.*)?$/;

/**
 * `slugs` are the backend's listed file names (case-sensitive). Everything that
 * is not an external link, an in-page anchor or a bare listed `<name>.md` is
 * repository-only: any `../` path, any other directory, any unlisted name.
 */
export function resolveDocLink(href: string | undefined, slugs: ReadonlySet<string>): DocLink {
  if (!href) return { kind: 'repo-only' };
  if (EXTERNAL_PROTOCOL.test(href)) return { kind: 'external', href };
  if (href.startsWith('#')) return { kind: 'anchor', href };
  const match = PUBLIC_DOC_HREF.exec(href.startsWith('./') ? href.slice(2) : href);
  const name = match?.[1];
  if (name && slugs.has(name)) {
    return { kind: 'doc', to: `/docs/${name}${match[2] ?? ''}` };
  }
  return { kind: 'repo-only' };
}
